import { getRequirementsTree } from '@bsa/scouting-js-utils/src/advancements';
import { statusValues } from '@bsa/scouting-js-utils/src/constants/advancements';
import type { Rule } from 'antd-v5/es/form';
import { DateTime } from 'luxon';

import {
  awardRequirementIdsWithoutApproveOption,
  boardOfReviewOrConferenceRequirementIds,
  ranksRequirementIdsWithoutApproveOption,
} from '@shared/constants';
import { intl } from '@shared/localization';
import { parseStatus, tagStatus } from '@shared/utils/advancementDisplay';
import advancementStatus from '@shared/utils/advancementStatus';
import { isAfterToday } from '@utils/datesLuxon';

import { AdvancementAction, FormField } from '../AdvancementDetails/types';
import type { SupportedAdvancementType } from '../AdvancementDetails/types';
import type {
  CatalogRequirement,
  RequirementDisableResult,
  RequirementFormValues,
  RequirementNode,
  RequirementRowPolicy,
  RequirementsFormData,
  VersionOption,
  YouthRequirement,
} from './types';
import { VersionTimeFrame } from './types';

/** The antd `Form` key the whole requirement map lives under */
export const REQUIREMENTS_FIELD = 'requirements';

/**
 * The `Form.Item` paths for one requirement. The id is stringified because
 * rc-field-form reads a numeric path segment as an array index, allocating a
 * sparse array instead of an object key
 */
export function requirementNamePaths(id: number | string) {
  const key = String(id);
  return [
    [REQUIREMENTS_FIELD, key, FormField.CompletionDate],
    [REQUIREMENTS_FIELD, key, FormField.Approved],
  ];
}

function toBoolean(value: unknown): boolean {
  return value === true || value === 'True' || value === 'true';
}

function toNumberOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
}

/**
 * Joins catalog text to youth progress. Ids are version-scoped, so both sides
 * must come from the same `versionId` or nothing matches
 */
export function mergeRequirements(
  catalog: CatalogRequirement[] = [],
  youth: YouthRequirement[] = [],
): RequirementNode[] {
  const youthById = new Map<number, YouthRequirement>(
    youth.map(item => [Number(item.id), item]),
  );

  return catalog.map(catalogItem => {
    const id = Number(catalogItem.id);
    const progress = youthById.get(id);
    const merged = { ...catalogItem, ...progress } as Record<string, unknown>;

    return {
      ...merged,
      id,
      versionId: Number(merged.versionId),
      parentRequirementId: toNumberOrNull(merged.parentRequirementId),
      childrenRequired: Number(merged.childrenRequired) || 0,
      required: toBoolean(merged.required),
      previousRankRequired: toBoolean(merged.previousRankRequired),
      disabledOnQuickEntry: toBoolean(merged.disabledOnQuickEntry),
    } as unknown as RequirementNode;
  });
}

/**
 * Nests by `parentRequirementId`. Ordering falls back to `listNumber`: only the
 * newest version of a rank populates `sortOrder`
 */
export function buildRequirementsTree(
  merged: RequirementNode[],
): RequirementNode[] {
  return getRequirementsTree(merged) as RequirementNode[];
}

/** Depth-first walk, parents before their children */
export function flattenRequirements(
  nodes: RequirementNode[] = [],
): RequirementNode[] {
  return nodes.flatMap(node => [
    node,
    ...flattenRequirements(node.requirements),
  ]);
}

/** Seeds the consuming `<Form>`; keys are stringified ids */
export function buildRequirementsInitialValues(
  nodes: RequirementNode[] = [],
): RequirementsFormData {
  const entries = flattenRequirements(nodes).map(node => {
    // the tree builder rewrites a missing date to `''`, so check truthiness
    const completed = node.dateCompleted
      ? DateTime.fromISO(node.dateCompleted)
      : null;

    const values: RequirementFormValues = {
      [FormField.CompletionDate]: completed?.isValid ? completed : null,
      [FormField.Approved]: Boolean(node.leaderApprovedDate),
    };

    return [String(node.id), values] as const;
  });

  return { [REQUIREMENTS_FIELD]: Object.fromEntries(entries) };
}

/**
 * A factory, not a constant, so the message resolves at validation time rather
 * than at import, when `intl` is still null
 */
export function noFutureDateRule(): Rule {
  return {
    validator: (_rule, value: DateTime | null) =>
      !value || !isAfterToday(value)
        ? Promise.resolve()
        : Promise.reject(
            new Error(
              intl.formatMessage({ id: 'requirementsRenderer.futureDate' }),
            ),
          ),
  };
}

function isEmptyish(value: unknown): boolean {
  if (value === null || value === undefined || value === '') return true;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return false;
}

/**
 * The `StatusTag` variant, taking whichever of two sources claims the more
 * advanced state. The audit dates catch the API under-reporting an approval, but
 * `getStatus` knows only the raw `"Started"` string and the tree builder has
 * already rewritten `status` to UPPER_SNAKE; meanwhile the API reports Eagle
 * requirement 1 as started with no `dateStarted` at all
 */
export function resolveRequirementStatus(requirement: RequirementNode): string {
  const fromDates = parseStatus(advancementStatus.getStatus(requirement));
  const fromApi = parseStatus(requirement.status);

  const winner =
    (statusValues[fromApi] ?? 0) > (statusValues[fromDates] ?? 0)
      ? fromApi
      : fromDates;

  return tagStatus(winner);
}

/**
 * Whether a row takes edit controls at all. Mirrors `isUpdateableRequirement`
 * (`bulkEntry/duck/utils/requirementUtils.ts`) but reads the coerced shape: that
 * version's `parseStringBoolean` answers `false` for a real boolean `true`
 */
export function isRowUpdateable(requirement: RequirementNode): boolean {
  const isParent = requirement.childrenRequired > 0;

  const hasLinkedMeritBadges = [
    Number(requirement.eagleMBRequired),
    Number(requirement.totalMBRequired),
  ].some(Boolean);

  const isSeaScoutElective =
    Boolean(requirement.requiresSSElective) ||
    !isEmptyish(requirement.ssElectives);

  const hasLinkedAdventures = [
    requirement.linkedAdventure,
    requirement.linkedAdventureId,
    requirement.linkedAwardId,
    requirement.linkedElectiveAdventures,
  ].some(value => !isEmptyish(value));

  return (
    !isParent &&
    !hasLinkedMeritBadges &&
    !isSeaScoutElective &&
    !hasLinkedAdventures &&
    !requirement.disabledOnQuickEntry
  );
}

/**
 * A row with no number and no parent is prose, not a requirement. Same rule as
 * `shouldRequirementDisplayOnly` in `youthProfile/utils`
 */
export function isDisplayOnlyRow(requirement: RequirementNode): boolean {
  return !requirement.listNumber && requirement.parentRequirementId === null;
}

function approveHiddenFor(
  requirement: RequirementNode,
  advancementType: SupportedAdvancementType,
): boolean {
  const list =
    advancementType === 'awards'
      ? awardRequirementIdsWithoutApproveOption
      : ranksRequirementIdsWithoutApproveOption;

  return list.includes(requirement.id);
}

export interface ResolveRowPolicyInput {
  requirement: RequirementNode;
  advancementType: SupportedAdvancementType;
  /** Greyed on every row by the page's action policy */
  disabledActions?: AdvancementAction[];
  /** The viewer is a youth acting on a record they may edit */
  isYouthEditor?: boolean;
  disableRequirement?: (
    id: number,
    requirement: RequirementNode,
  ) => RequirementDisableResult;
}

/**
 * Folds every policy layer into one answer. Additive: a layer may take a control
 * away and none may give one back
 */
export function resolveRowPolicy(
  input: ResolveRowPolicyInput,
): RequirementRowPolicy {
  const {
    requirement,
    advancementType,
    disabledActions = [],
    isYouthEditor = false,
    disableRequirement,
  } = input;

  const readOnly =
    !isRowUpdateable(requirement) || isDisplayOnlyRow(requirement);

  const disabled: Partial<Record<AdvancementAction, boolean>> = {};

  disabledActions.forEach(action => {
    disabled[action] = true;
  });

  // run by the unit committee and the unit leader respectively, so neither is a
  // youth's to record for themselves at any tier
  if (
    isYouthEditor &&
    boardOfReviewOrConferenceRequirementIds.includes(requirement.id)
  ) {
    disabled[AdvancementAction.Approve] = true;
    disabled[AdvancementAction.CompletionDate] = true;
  }

  const consumer = disableRequirement?.(requirement.id, requirement);
  if (consumer === true) {
    disabled[AdvancementAction.CompletionDate] = true;
    disabled[AdvancementAction.Approve] = true;
  } else if (consumer && typeof consumer === 'object') {
    (Object.keys(consumer) as AdvancementAction[]).forEach(action => {
      if (consumer[action]) disabled[action] = true;
    });
  }

  return {
    disabled,
    hideApprove: approveHiddenFor(requirement, advancementType),
    readOnly,
  };
}

export function getRequirementSaveErrorMessage(
  error: unknown,
): string | undefined {
  // Only ever the parsed body, FYI a bare `Error` also has a `message`
  const body = Array.isArray(error)
    ? error
    : (error as { data?: unknown })?.data;

  if (Array.isArray(body)) {
    const messages = body
      .flatMap(
        entry => (entry as { requirements?: unknown })?.requirements ?? [],
      )
      .filter(
        (requirement): requirement is { status: string; message?: string } =>
          (requirement as { status?: string })?.status === 'Fail',
      )
      .map(requirement => requirement.message?.trim())
      .filter((message): message is string => Boolean(message));

    // de-duplicated: one rejected rank requirement often repeats its reason
    return [...new Set(messages)].join(' ') || undefined;
  }

  if (!body || typeof body !== 'object') return undefined;

  const message = (body as { message?: unknown }).message;
  return typeof message === 'string' && message.trim()
    ? message.trim()
    : undefined;
}

export interface RequirementsFetchState {
  /** Whether either request is in flight */
  youthFetching: boolean;
  catalogFetching: boolean;
  /** True once the catalog for the *current* rank and version has arrived */
  hasCatalog: boolean;
  /** Whether merged rows are already on screen */
  hasRequirements: boolean;
  isError: boolean;
}

/**
 * Whether the checklist shows its skeleton or just the <Spin />
 */
export function isRequirementsLoading(state: RequirementsFetchState): boolean {
  if (state.isError) return false;
  if (state.hasRequirements) return false;
  return state.youthFetching || state.catalogFetching || !state.hasCatalog;
}

/**
 * Labels a version against today. `expiredDate` is the Scoutbook-side
 * retirement, distinct from `versionExpiryDt`
 */
export function toVersionOptions(
  versions: Omit<VersionOption, 'timeFrame'>[] = [],
): VersionOption[] {
  const today = DateTime.now().startOf('day');

  return versions.map(version => {
    const expired = version.expiredDate
      ? DateTime.fromISO(version.expiredDate) < today
      : false;
    const past = version.versionExpiryDt
      ? DateTime.fromISO(version.versionExpiryDt) < today
      : false;

    let timeFrame = VersionTimeFrame.Active;
    if (expired) timeFrame = VersionTimeFrame.Archived;
    else if (past) timeFrame = VersionTimeFrame.Past;

    return { ...version, timeFrame };
  });
}
